// Globális app-shell — a Claude Design "Atlas" terv szerint (docs/foundations/design.md):
// 240px sidebar (logó + szekcionált nav — CSAK navigáció),
// 52px topbar (cím·leírás + eszköz-ikonok: kereső ⌘K → AI → harang → avatar-menü). Reszponzív: mobilon drawer; az
// összecsukás edge-toggle-lel (ikon-sáv). Csak a tartalom görög — a shell fix.
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LanguageIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AtlasMark } from '@/components/brand/atlas-logo';
import { MO_ENABLED } from '@/components/mo/mo-config';
import { MoPanel } from '@/components/mo/mo-panel';
import { CommandPalette } from '@/components/shared/command-palette';
import { MobileActionFab, type FabAction } from '@/components/shared/mobile-action-fab';
import { navGroups, navItemVisible } from '@/components/shared/nav-config';
import { ThemeModeItems } from '@/components/theme-toggle';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Text } from '@/components/ui/text';
import { ImpersonationBanner } from '@/features/impersonation/components/impersonation-banner';
import { NotificationBell } from '@/features/notifications/components/notification-bell';
import { api } from '@/lib/api';
import { logoutEverywhere } from '@/lib/auth';
import { formatName } from '@/lib/format-name';
import { usePermissions } from '@/lib/use-permissions';
import { cn } from '@/lib/utils';

const COLLAPSE_KEY = 'atlas:sidebar-collapsed';

// Nav-on kívüli oldalak (nincsenek a sidebar-nav-ban, az avatar-menüből nyílnak) topbar-címe.
// ALCÍM (`descKey`) SZÁNDÉKOSAN NINCS: a leíró mondat a LISTA-oldalak sajátja (ott a nav-bejegyzés adja);
// a form-/detail-/beállítás-oldalakon a cím (+ a szülő-morzsa) önmagában elég — lásd `design.md`.
const EXTRA_PAGE_TITLES: Record<string, { labelKey: string; descKey?: string }> = {
  '/profile': { labelKey: 'profile.title' },
};
/** Összecsukott rail — 28×28 négyzet (h-7), ikon középen → egyenlő padding minden oldalon; mx-auto: középre a railben. */
const COLLAPSED_NAV_ITEM =
  'mx-auto flex size-7 shrink-0 items-center justify-center gap-0 p-0 leading-none';

function nonEmpty(v: string | null | undefined): string | undefined {
  const x = v?.trim();
  if (!x) return undefined;
  return x;
}

/** A bejelentkezett user locale-tudatos neve + emailje + avatar-URL-je + részleg-színe (a /session-ből). */
function useProfile(): {
  name: string;
  email: string | null;
  avatarUrl: string | null;
  teamColor: string | null;
} {
  const { i18n } = useTranslation();
  const q = api.session.useQuery(['session'], {});
  const s = q.data?.status === 200 ? q.data.body : undefined;
  const name = formatName(
    {
      givenName: s?.givenName,
      familyName: s?.familyName,
      displayName: nonEmpty(s?.name) ?? nonEmpty(s?.email),
    },
    i18n.language,
  );
  return {
    name,
    email: s?.email ?? null,
    avatarUrl: s?.avatarUrl ?? null,
    teamColor: s?.teamColor ?? null,
  };
}

/** Brand-fejléc a sidebar tetején: logó + Atlas — semmi más. A mobil drawerben `flat` (lap nélküli, gradiens
 *  konstelláció); a desktop rail-en marad az alap (gradiens háttér-lap + fehér konstelláció). LINK a kezdőlapra
 *  (Évi #88 — a megszokott „logó = haza" minta); a mobil drawerben a kattintás a drawert is bezárja. */
function WorkspaceHeader({
  collapsed,
  flat = false,
  onNavigate,
}: {
  collapsed: boolean;
  flat?: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Link
      to="/"
      aria-label={t('nav.home')}
      {...(onNavigate ? { onClick: onNavigate } : {})}
      className={cn(
        'focus-visible:ring-ring flex h-[52px] w-full shrink-0 items-center gap-2.5 rounded-md px-3 focus-visible:outline-none focus-visible:ring-2',
        collapsed && 'justify-center px-0',
      )}
    >
      <AtlasMark size={26} flat={flat} />
      {!collapsed && (
        <span className="text-lg font-semibold leading-none tracking-[-0.005em]">Atlas</span>
      )}
    </Link>
  );
}

/** A navigáció (desktop rail + mobil drawer közös tartalma), jog-vezérelten szűrve.
 *  A feliratos csoportok CSUKHATÓK (Notion-minta: kattintható felirat, hoverre megjelenő chevron),
 *  az állapot localStorage-ban; a felirat nélküli szakaszok fixek. Kompakt sorok (28px). */
const NAV_GROUPS_KEY = 'atlas:nav-groups';

function SidebarNav({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) ?? '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  });
  const toggleGroup = (key: string): void =>
    setClosedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(next));
      return next;
    });

  const groups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((i) => navItemVisible(i, can)) }))
    .filter((g) => g.items.length > 0);

  return (
    <nav
      className={cn(
        'scrollbar-hover flex flex-1 flex-col overflow-y-auto',
        // Összecsukva a görgetősáv helyét SZIMMETRIKUSAN foglaljuk (bal+jobb), különben a jobb oldali 8px
        // a középre igazított (mx-auto) ikonokat balra tolná a logó középpontjához képest. Nyitva nem kell
        // (a menüpontok balra igazítottak, azokat a scrollbar nem tolja).
        // Felső köz `pt-3` (12px): a fejléc alatti térköz egyezzen a topbar-cím alatti köszzel (a toolbar h-14
        // + h-8 keresőmező → 12px). Így bal (Atlas→Áttekintés) és jobb (cím→kereső) alatt ugyanakkora a rés.
        // az alsó térköz a home-indicator sávjával együtt (a mobil menüben a lista így a képernyő aljáig fut)
        collapsed
          ? 'px-0 pb-[calc(0.375rem+env(safe-area-inset-bottom))] pt-3 [scrollbar-gutter:stable_both-edges]'
          : 'px-2 pb-[calc(0.375rem+env(safe-area-inset-bottom))] pt-3',
      )}
    >
      {groups.map((group, gi) => {
        const closed = group.labelKey ? !!closedGroups[group.labelKey] : false;
        const hasActive = group.items.some((i) => i.to === pathname);
        return (
          <div key={group.labelKey ?? `g${gi}`} className="flex flex-col gap-px">
            {group.labelKey && !collapsed && (
              <button
                type="button"
                onClick={() => toggleGroup(group.labelKey!)}
                className="text-muted-foreground hover:text-foreground group flex w-full items-center gap-1.5 px-1.5 pb-0.5 pt-2 transition-colors"
              >
                {/* A tipográfiát (11/600/CAPS) az `overline` adja; a színt/hovert a gomb vezérli → `text-inherit`.
                    A csoportcím EGY SOR: a `whitespace-nowrap` tiltja a tördelést (a leghosszabb címke —
                    „Támogatás és kommunikáció" — a szűk sávban két sorra esett, és ilyenkor az egész
                    nav-blokk elcsúszott), a `truncate` pedig a degradáció: inkább „…", mint tördelés. */}
                <Text
                  variant="overline"
                  as="span"
                  className="min-w-0 truncate whitespace-nowrap text-inherit"
                >
                  {t(group.labelKey)}
                </Text>
                {closed && hasActive && (
                  <span className="bg-brand size-1 shrink-0 rounded-full" aria-hidden="true" />
                )}
                <ChevronDownIcon
                  className={cn(
                    'ml-auto size-3 shrink-0 opacity-0 transition-[opacity,transform] group-hover:opacity-100',
                    closed && '-rotate-90',
                  )}
                />
              </button>
            )}
            {/* Összecsukva: az elválasztó-blokk pont olyan magas (24px), mint nyitva a csoportcím-sor
                (pt-2 + 14px sormagasság + pb-0.5) → az ikonok nem mozdulnak függőlegesen ki-be csukáskor. */}
            {group.labelKey && collapsed && (
              <div className="flex h-6 items-center justify-center" aria-hidden="true">
                <Divider inset={false} spacing="none" className="w-6" />
              </div>
            )}
            {(!closed || collapsed) &&
              group.items.map((item) =>
                item.to ? (
                  <Link
                    key={item.labelKey}
                    to={item.to}
                    onClick={onNavigate}
                    activeOptions={{ exact: item.to === '/' }}
                    title={collapsed ? t(item.labelKey) : undefined}
                    className={cn(
                      'text-muted-foreground ease-atlas hover:bg-accent hover:text-foreground relative flex rounded-md border border-transparent transition-colors duration-150',
                      '[&_svg]:text-muted-foreground [&_svg]:size-4 [&_svg]:shrink-0',
                      collapsed ? COLLAPSED_NAV_ITEM : 'h-7 items-center gap-1.5 pl-1 pr-2',
                    )}
                    activeProps={{
                      // DocuShare SidebarItem aktív-stílusa: zöld gradiens-derengés + zöld keret + zöld szöveg/ikon.
                      // A felirat `text-inherit`-tel örökli a Link színét → az aktív zöldet `!`-tal KÉNYSZERÍTJÜK
                      // (mint az ikonét), különben a base `text-muted-foreground`-dal CSS-sorrend szerint versengene
                      // (világosban a muted nyert → a felirat nem lett zöld, míg az ikon igen).
                      className:
                        '!text-accent-600 dark:!text-accent-400 from-accent-500/20 to-accent-400/20 border-accent-500/30 [&_svg]:!text-accent-600 dark:[&_svg]:!text-accent-400 bg-gradient-to-r font-medium',
                    }}
                  >
                    {collapsed ? (
                      <item.icon />
                    ) : (
                      <span className="flex w-[26px] shrink-0 justify-center">
                        <item.icon />
                      </span>
                    )}
                    {/* A menüpont-felirat tipográfiája `body` (13/400 — főnavigáció best practice-e 13–14px, nem
                        caption-méret); a színt (rest/hover/aktív) a Link vezérli → `text-inherit`, hogy az aktív
                        accent-szín cascade-elhessen, az aktív `font-medium` pedig a súlyt emelje. */}
                    {!collapsed && (
                      <Text variant="body" as="span" className="truncate text-inherit">
                        {t(item.labelKey)}
                      </Text>
                    )}
                  </Link>
                ) : (
                  <span
                    key={item.labelKey}
                    title={t('nav.comingSoon')}
                    className={cn(
                      'text-muted-foreground/40 flex cursor-not-allowed rounded-md [&_svg]:size-4 [&_svg]:shrink-0',
                      collapsed ? COLLAPSED_NAV_ITEM : 'h-7 items-center gap-1.5 pl-1 pr-2',
                    )}
                  >
                    {collapsed ? (
                      <item.icon />
                    ) : (
                      <span className="flex w-[26px] shrink-0 justify-center">
                        <item.icon />
                      </span>
                    )}
                    {!collapsed && (
                      // Inaktív (coming-soon) menüpont: CSAK muted (a szülő `text-muted-foreground/40`),
                      // de UGYANAKKORA méret, mint az aktív menüpontok (`body`/13px) — NEM kisebb.
                      <Text variant="body" as="span" className="truncate text-inherit">
                        {t(item.labelKey)}
                      </Text>
                    )}
                  </span>
                ),
              )}
          </div>
        );
      })}
    </nav>
  );
}

/** Topbar-avatar menü: név/email + téma- és nyelvváltó + kijelentkezés (a globális beállítások egy helye). */
function UserMenu(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { name, email, avatarUrl, teamColor } = useProfile();
  const nextLang = i18n.resolvedLanguage === 'en' ? 'hu' : 'en';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="focus-visible:ring-ring focus-visible:ring-offset-background hover:bg-accent flex size-9 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          aria-label={name}
        >
          <Avatar name={name} src={avatarUrl} size="md" accentColor={teamColor} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        // Bezáráskor NE adja vissza a fókuszt a triggerre (Radix-alap) — különben a programozott `.focus()`-t a
        // böngésző billentyűzet-fókusznak veszi, és rajta ragad a zöld focus-visible ring. A valódi Tab-fókusz
        // ringje így is megmarad (a11y). Egérrel csukva a fókusz a body-ra kerül → nincs ragadó ring.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>
          <div className="truncate font-medium">{name}</div>
          {email && (
            <div className="text-muted-foreground truncate text-xs font-normal">{email}</div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => void navigate({ to: '/profile' })}>
          <UserCircleIcon />
          {t('profile.menu')}
        </DropdownMenuItem>
        {/* Téma: három mód (világos · sötét · rendszer) — a menü nyitva marad, lehet egymás után váltani */}
        <DropdownMenuSeparator />
        <ThemeModeItems />
        <DropdownMenuSeparator />
        {/* Toggle: preventDefault → a menü nyitva marad */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            void i18n.changeLanguage(nextLang);
          }}
        >
          <LanguageIcon />
          {nextLang === 'en' ? 'English' : 'Magyar'}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={() => {
            void logoutEverywhere().finally(() => void navigate({ to: '/login' }));
          }}
        >
          <ArrowRightStartOnRectangleIcon />
          {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppShell({
  children,
  toolbar,
  primaryAction,
  secondaryActions,
  hideFab = false,
  title: titleProp,
  subtitle: subtitleProp,
  parent,
}: {
  children: ReactNode;
  /** Opcionális, FIX eszköztár-sáv a navbar alatt (a main-scrollon KÍVÜL — így semmi nem görög alatta, és
   *  átlátszó marad, mint a navbar). A lista-oldalak ide teszik a kereső/szűrő/„új" sort; a tábla-fejléc a
   *  main-ben `sticky top-0`-val e sáv alá pinnel. */
  toolbar?: ReactNode;
  /** Az oldal elsődleges akciója (pl. „+ Új …") — MOBILON a lebegő „speed dial" FAB-ban jelenik meg (Keresés · AI
   *  mellett). Desktopon az oldal a saját toolbar-jába teszi a gombot; ez a prop csak a mobil-FAB-ot táplálja. */
  primaryAction?: { icon: ReactNode; label: string; onClick: () => void } | undefined;
  /**
   * A lap MÁSODLAGOS akciói — MOBILON a lebegő FAB-ba kerülnek, a „Kérdezd Mo-t" ALÁ és az elsődleges akció
   * FÖLÉ. Desktopon a lap a saját eszköztárába teszi őket; ez a prop CSAK a mobil-FAB-ot táplálja.
   *
   * MIÉRT KELL: a sáv-gombok mobilon el vannak rejtve (375 px-en a kereső + „Szűrők" az elsődleges), tehát
   * az export, a „Lekérés a NAV-ból", a „Kategóriák" és társaik e nélkül MOBILON ELÉRHETETLENEK — a lap
   * némán kevesebbet tudott, mint deszktopon.
   */
  secondaryActions?:
    { key: string; icon: ReactNode; label: string; onClick: () => void }[] | undefined;
  /** A mobil lebegő gomb (FAB) elrejtése — pl. a kezdőlap szerkesztő-módjában, ahol a ragadós alsó sáv foglalja a helyet. */
  hideFab?: boolean;
  /** A topbar morzsa címe. Az al-útvonalaknak (create/edit/detail) NINCS nav-bejegyzésük, ezért nélküle a
   *  `t('app.name')` fallbackre esnének — „Atlas" lenne a címük. A nav-oldalak nem adják meg (a nav-ból jön). */
  title?: string | undefined;
  /** A cím melletti leírás a morzsában (CSAK desktopon látszik — `lg:inline`). Csak `title`-lel értelmes.
   *  Tartsd rövidnek és állandónak: ez az oldal jellegét mondja meg, NEM adat-részletet (darabszám, státusz). */
  subtitle?: string | undefined;
  /** Szülő-morzsa a cím ELŐTT: `{label} / **{title}**`. A `label` kattintható link a `to`-ra — ez a
   *  vissza-navigáció helye a form-/detail-oldalakon (nincs külön „Vissza" gomb az űrlapon). */
  parent?: { label: string; to: string } | undefined;
}): JSX.Element {
  // PARTNER a BELSŐ felületen → a portálra irányítjuk. A felület-választás a `/session` `type`-jából dől el
  // (egy belépő-felület, egy cím — user-döntés); ez a `PortalShell` ellenkező irányú párja. A
  // jogosultsági FAL a szerveren van (a BFF belső végpontjai partner-tokenre 403-at adnak) — ez itt csak azt
  // akadályozza meg, hogy a partner egy néma, üres admin-felületen ragadjon.
  usePartnerRedirect();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const toggleCollapsed = (): void =>
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });

  // ⌘K / Ctrl+K → command-palette (a terv szerint).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = navGroups.flatMap((g) => g.items).find((i) => i.to === pathname);
  // Nav-on kívüli oldalak (az avatar-menüből érhetők el, nincsenek a sidebar-nav-ban) topbar-cím/leírás-térképe.
  const extraPage = !active ? EXTRA_PAGE_TITLES[pathname] : undefined;
  // Az oldal által ADOTT cím mindent felülír — ez fedi le a create/edit/detail al-útvonalakat, amiknek nincs
  // nav-bejegyzésük (és a DINAMIKUSAKAT is, amiket egy pathname-kulcsos térkép nem tudna kezelni).
  const navTitle = active ? t(active.labelKey) : extraPage ? t(extraPage.labelKey) : t('app.name');
  const navSubtitle = active?.descKey
    ? t(active.descKey)
    : extraPage?.descKey
      ? t(extraPage.descKey)
      : undefined;
  const title = titleProp ?? navTitle;
  // Ha az oldal ad címet, a leírás is TŐLE jön (különben a nav leírása ragadna be egy al-oldalra).
  const subtitle = titleProp ? subtitleProp : navSubtitle;

  // Mo (AI-asszisztens) jobb oldali panel — a topbar/FAB csillag-ikonjáról nyílik.
  const [aiOpen, setAiOpen] = useState(false);

  // A mobil speed-dial FAB akciói: Keresés · AI · (ha van) az oldal elsődleges akciója (kiemelve, a FAB-hoz közel).
  const fabActions: FabAction[] = [
    {
      key: 'search',
      icon: <MagnifyingGlassIcon />,
      label: t('nav.search'),
      onClick: () => setPaletteOpen(true),
    },
    // a Mo ideiglenesen rejtve (`MO_ENABLED`, #106)
    ...(MO_ENABLED
      ? [
          {
            key: 'ai',
            icon: <SparklesIcon className="text-brand" />,
            label: 'Kérdezd Mo-t…',
            onClick: () => setAiOpen(true),
          },
        ]
      : []),
    // MÁSODLAGOS akciók — a „Kérdezd Mo-t" ALATT, az elsődleges FÖLÖTT (a FAB-hoz közelebb a fontosabb).
    ...(secondaryActions ?? []),
    ...(primaryAction
      ? [
          {
            key: 'primary',
            icon: primaryAction.icon,
            label: primaryAction.label,
            onClick: primaryAction.onClick,
            highlight: true,
          },
        ]
      : []),
  ];

  return (
    // A KERET MAGASSÁGA `100dvh` (`app-viewport`; telepített iOS-appban `100vh` — l. `globals.css`), a felső és
    // oldalsó biztonsági zónát a keret veszi fel (`app-safe-area`), az ALSÓT a görgetett tartalom (`app-safe-bottom`
    // a `main`-en) — így a tartalom a home-indicator alá is lefut. Asztali gépen egyik osztály sem változtat semmit.
    <div className="app-viewport app-safe-area flex overflow-hidden">
      {/* Desktop sidebar (fix, összecsukható) */}
      <aside
        className={cn(
          // Lebegő üveg-panel: margin körben (bal/fent/lent + jobb rés a tartalomtól) + erős lekerekítés.
          'sidebar-glass group/aside relative m-2 hidden shrink-0 flex-col rounded-2xl md:flex',
          collapsed ? 'w-[50px]' : 'w-60',
        )}
      >
        <WorkspaceHeader collapsed={collapsed} />
        <SidebarNav collapsed={collapsed} />
        {/* Edge-toggle: a sidebar jobb szélén, függőlegesen középen — csak a sidebar hoverjére
            úszik be (Jira/Notion-minta); Tab-fókusznál is megjelenik (a11y). */}
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={t('nav.collapse')}
          className="bg-card text-muted-foreground hover:bg-accent hover:text-foreground absolute -right-3 top-1/2 z-20 flex size-6 -translate-y-1/2 items-center justify-center rounded-full border opacity-0 shadow-sm transition-[opacity,background-color,color] duration-150 focus-visible:opacity-100 group-hover/aside:opacity-100"
        >
          {collapsed ? (
            <ChevronRightIcon className="size-3.5" />
          ) : (
            <ChevronLeftIcon className="size-3.5" />
          )}
        </button>
      </aside>

      {/* Mobil drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0">
          <SheetTitle className="sr-only">Atlas</SheetTitle>
          <WorkspaceHeader collapsed={false} flat onNavigate={() => setMobileOpen(false)} />
          <SidebarNav collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Mo — AI-asszisztens (jobb oldali panel; a topbar/FAB csillag-ikonjáról nyílik) — ideiglenesen rejtve */}
      {MO_ENABLED && (
        <Sheet open={aiOpen} onOpenChange={setAiOpen}>
          <SheetContent
            side="right"
            hideClose
            // MOBILON TELJES SZÉLESSÉG, deszktopon fix panel — ugyanaz a geometria, mint minden más beúszónál
            // (`FormSheet`): a 400 px-es fix szélesség telefonon egy keskeny hasábot hagyott, a lap pedig
            // félig kilátszott mellőle. Egy app, egy beúszó-alak.
            className="p-0"
          >
            {/* A záró X-et a MoPanel fejléce adja (a „Mo" címmel egy vonalban) → a beépített abszolút X-et
              elrejtjük; a SheetTitle-t (a11y) is a MoPanel szolgáltatja. */}
            <MoPanel />
          </SheetContent>
        </Sheet>
      )}

      {/* Fő terület — csak a tartalom görög, a topbar fix */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Topbar — beleolvad az app-layout hátterébe (bg-background), NINCS üveg/elválasztó vonal.
            Magasság 60px + felső 8px padding → a tartalom középvonala a lebegő sidebar (m-2 = 8px felső
            margó) Atlas-logójának középvonalára esik, így egy vonalban vannak. */}
        <header className="flex h-[60px] shrink-0 items-center gap-2.5 bg-transparent px-4 pt-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-9 md:hidden [&_svg]:size-[18px]"
            aria-label={t('nav.menu')}
            onClick={() => setMobileOpen(true)}
          >
            <Bars3Icon />
          </Button>
          {/* Morzsa: cím (title, 16/600/primary) · leírás (title-MÉRET 16, de muted/normál — a hierarchiát a
              súly+szín viszi, nem a méret); a pont körül tágasabb köz (mx-2). */}
          <div className="flex min-w-0 items-baseline">
            {/* SZÜLŐ-morzsa (form-/detail-oldalakon): „Ügyfelek / **Ügyfél neve**" — a szülő KATTINTHATÓ, ez a
                vissza-út. Ezért NINCS külön „Vissza a …" gomb az űrlapokon: a navigáció helye a morzsa, nem az
                akció-sor. A lista-oldalak nem adják meg (nekik nincs szülőjük). */}
            {parent && (
              <>
                <Link
                  to={parent.to}
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring shrink-0 rounded-sm transition-colors focus:outline-none focus-visible:ring-1"
                >
                  <Text variant="title" as="span" className="font-normal text-inherit">
                    {parent.label}
                  </Text>
                </Link>
                <Text
                  variant="title"
                  tone="secondary"
                  as="span"
                  className="mx-1.5 shrink-0 font-light"
                  aria-hidden="true"
                >
                  /
                </Text>
              </>
            )}
            <Text variant="title" as="span" className="truncate">
              {title}
            </Text>
            {subtitle && (
              <>
                <Text
                  variant="title"
                  tone="secondary"
                  as="span"
                  className="mx-2 hidden font-normal lg:inline"
                  aria-hidden="true"
                >
                  ·
                </Text>
                <Text
                  variant="title"
                  tone="secondary"
                  as="span"
                  className="hidden truncate font-light lg:inline"
                >
                  {subtitle}
                </Text>
              </>
            )}
          </div>
          <div className="ml-auto flex items-center gap-0.5">
            {/* Kereső ⌘K + AI: mobilon az ALSÓ akció-sávban vannak → itt csak ≥md. */}
            <div className="hidden items-center gap-0.5 md:flex">
              <Button
                variant="ghost"
                size="icon"
                className="size-9 [&_svg]:size-[18px]"
                title={`${t('nav.search')} (⌘K)`}
                aria-label={t('nav.search')}
                onClick={() => setPaletteOpen(true)}
              >
                <MagnifyingGlassIcon />
              </Button>
              {MO_ENABLED && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9 [&_svg]:size-[18px]"
                  title="Kérdezd Mo-t…"
                  aria-label="Kérdezd Mo-t"
                  onClick={() => setAiOpen(true)}
                >
                  <SparklesIcon className="text-brand" />
                </Button>
              )}
            </div>
            {/* Harang + avatar: MINDEN méreten a felső sávban (identitás + értesítés). */}
            <NotificationBell />
            <UserMenu />
          </div>
        </header>
        {/* MEGSZEMÉLYESÍTÉS-SÁV — a TOPBAR ALATT, de a `main`-scrollon KÍVÜL (a keret fix eleme), tehát
            görgetés közben is látszik. A `mx-4` az app igazítási vonala: ezen áll a `ListToolbar`, a
            lista-lapok tartalma, a `FormPage` (`md:px-4`) és a kezdőlap `PageBody align="left"`-ja is —
            így a sáv a LAP szélességén ül, nem edge-to-edge csíkként vágja át a felületet.
            A `main` FÖLÖTT van, nem benne: a flex-kolumna így vonja le a magasságát, és a `h-full`
            lista-lapok rögzített `DataTable`-kártyája sem csúszik ki (a scrollon belül ez elromlott volna). */}
        <ImpersonationBanner className="mx-4 mt-3" />
        {/* Fix eszköztár-sáv (a main-scrollon kívül, átlátszó — mint a navbar) */}
        {toolbar && <div className="shrink-0">{toolbar}</div>}
        {/* `relative`: ez teszi a `main`-t CONTAINING BLOCK-ká minden abszolút pozicionált leszármazottjának.
            Enélkül egy pozicionált ős nélküli `absolute` elem (tipikusan a Tailwind `sr-only`, ami
            `position:absolute`) a VIEWPORT-dobozhoz igazodik, ezért a shell `overflow-hidden`-je NEM vágja le
            — a statikus pozíciója viszont a görgetett tartalom mélyén van, így megnyújtja a DOKUMENTUM
            görgethető magasságát, és megjelenik egy MÁSODIK, külső görgetősáv (a profil-oldalon pontosan ez
            történt: a jelszó-követelmények négy `sr-only` spanje 298px-cel nyújtotta meg a lapot).
            Az invariáns — „az app-shell tölti ki a viewportot, a görgetés BELÜL van" — enélkül nem áll. */}
        {/* `scrollbar-none`: a keret görgetője helyet NEM foglaló sávval megy — a klasszikus sáv 10px-et vett
            el a tartalom-dobozból, ezért a FOLYÓ lapok (Áttekintés, form-/detail-lapok) jobb széle 26px-re
            került a viewporttól, miközben a topbar avatarja és a toast 16px-en áll (user-lelet, 2026-09-09).
            A LISTA-lapokon a kérdés eleve nem jelent meg: azok `h-full`-osak, a görgetés a `DataTable`-ben
            van, tehát a keret nem görget. Ugyanez a döntés a partner-keretben (`portal-shell.tsx`) — a
            csere-üzlet ott van leírva. */}
        <main className="scrollbar-none app-safe-bottom relative flex-1 overflow-y-auto">
          {children}
        </main>
      </div>

      {/* Mobil „speed dial" FAB (<md): Keresés · AI · az oldal elsődleges akciója (kiemelve). A harang + avatar a
          felső sávban marad; a szekció-nav a drawerben. */}
      {!hideFab && <MobileActionFab actions={fabActions} />}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}

/**
 * PARTNER token a belső felületen → átirányítás a portálra (a `PortalShell` ellenkező irányú párja). A belső
 * mélylink PORTÁL-MEGFELELŐJÉRE visz, nem vakon a „Jegyeim"-re: egy régi (még belső címre mutató) értesítő-levél
 * `/messages?open=<id>` linkje így is a megnyitott üzenetre érkezik (kolléga-észrevétel #91).
 */
function usePartnerRedirect(): void {
  const navigate = useNavigate();
  const q = api.session.useQuery(['session'], {});
  const type = q.data?.status === 200 ? q.data.body.type : undefined;
  useEffect(() => {
    if (type !== 'partner') return;
    const { pathname, search } = window.location;
    const open = new URLSearchParams(search).get('open');
    if (pathname === '/messages') {
      void navigate({ to: '/portal/messages', search: open ? { open } : {}, replace: true });
    } else if (pathname === '/meetings') {
      void navigate({ to: '/portal/messages', replace: true });
    } else if (pathname === '/profile') {
      void navigate({ to: '/portal/profile', replace: true });
    } else {
      void navigate({ to: '/portal/tickets', replace: true });
    }
  }, [type, navigate]);
}
